import { useCallback, useMemo } from "react";

import {
  ArticleSection,
  ENTRY_DATA_SECTION_KEYS as SECTION_KEYS,
} from "@frontend/common";

import { ClassificationCode } from "@/types/code.types";
import { useGetPressContentBoldKeywordQuery } from "@api/pressContent/pressContent";
import { BoldKeywordItem } from "@api/pressContent/pressContent.interface";
import { ARTICLE_SECTION_CODE } from "@FillInTableContainer/constants/fillInTable.constants";
import { useEntryDataValue } from "@stores/entryData";
import { useCurrencyInfoValue } from "@stores/pressContentCurrencyKeyword";
import { getPressIdFromURL } from "@utils/getStateFromURL";

const PAYMENT_KEYWORDS_BAR = "__PAYMENT_KEYWORDS_BAR__";
const CURRENCY_BAR = "__CURRENCY_BAR__";
const UNIT_BAR = "__UNIT_BAR__";

interface BoldRange {
  start: number;
  end: number;
  type: "bold";
}

const generateBoldRanges = (
  text: string,
  keywordList: string[],
): BoldRange[] => {
  if (!keywordList?.length) return [];

  const ranges: BoldRange[] = [];

  const sortedKeywords = [...keywordList].sort((a, b) => b.length - a.length);

  sortedKeywords.forEach((keyword) => {
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedKeyword, "gi");
    let match: RegExpExecArray | null;

    regex.lastIndex = 0;

    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;

      ranges.push({
        start,
        end,
        type: "bold",
      });
    }
  });

  ranges.sort((a, b) => a.start - b.start);

  const mergedRanges: BoldRange[] = [];

  if (ranges.length === 0) {
    return mergedRanges;
  }

  let currentRange = ranges[0];

  for (let i = 1; i < ranges.length; i++) {
    if (ranges[i].start <= currentRange.end) {
      currentRange.end = Math.max(currentRange.end, ranges[i].end);
    } else {
      mergedRanges.push(currentRange);
      currentRange = ranges[i];
    }
  }
  mergedRanges.push(currentRange);

  return mergedRanges;
};

export const useCheckBoldKeywords = (
  classificationManual: ClassificationCode,
  additionalBoldKeywords: string[],
) => {
  // NOTE: classification manual 없는 경우에 대한 예외 처리 진행 (CTG)
  const { data: boldKeywords } =
    useGetPressContentBoldKeywordQuery(classificationManual);

  const articleSection = classificationManual
    ? ARTICLE_SECTION_CODE?.[classificationManual]
    : "";
  const entryData = useEntryDataValue(getPressIdFromURL());
  const boldKeywordsData = boldKeywords?.data as unknown as BoldKeywordItem[];

  const { paymentKeyword, currency, unit } = useCurrencyInfoValue();

  const currencyKeywordsBar = paymentKeyword.join("|");
  const currencyUnitBar = currency.join("|");
  const unitBar = unit.join("|");

  const amountRegexes = useMemo(() => {
    const currencySymbols = (currency || [])
      .filter(Boolean)
      .map((c) => c.replace(/\\\$/g, "$"));
    if (!currencySymbols.length) return [] as RegExp[];

    const escapedCurrencies = currencySymbols
      .map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .sort((a, b) => b.length - a.length);
    const currencyGroup = `(?:${escapedCurrencies.join("|")})`;

    const unitNames = (unit || []) as string[];
    const escapedUnits = unitNames
      .filter(Boolean)
      .map((u) => u.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .sort((a, b) => b.length - a.length);
    let unitGroup = "";
    if (escapedUnits.length) {
      unitGroup = `(?:\\s*(?:${escapedUnits.join("|")}))?`;
    }

    const numberGroup = "(?:\\d{1,3}(?:,\\d{3})*|\\d+)(?:\\.\\d+)?";
    const pattern = `${currencyGroup}\\s*${numberGroup}${unitGroup}`;
    return [new RegExp(pattern, "gi")];
  }, [currency, unit]);

  const paymentTemplateRegexes = useMemo(() => {
    const templates = (boldKeywordsData || [])
      .filter((k) => k?.isPayment === true)
      .map((k) => String(k.name));
    if (!templates.length) return [] as RegExp[];

    const currencySymbols = (currency || [])
      .filter(Boolean)
      .map((c) => c.replace(/\\\$/g, "$"));
    const escapedCurrencies = currencySymbols
      .map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .sort((a, b) => b.length - a.length);
    const currencyGroup = escapedCurrencies.length
      ? `(?:${escapedCurrencies.join("|")})`
      : "";

    const payments = (paymentKeyword || []) as string[];
    const escapedPayments = payments
      .filter(Boolean)
      .map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .sort((a, b) => b.length - a.length);
    const paymentGroup = escapedPayments.length
      ? `(?:${escapedPayments.join("|")})`
      : "";

    const units = (unit || []) as string[];
    const escapedUnits = units
      .filter(Boolean)
      .map((u) => u.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .sort((a, b) => b.length - a.length);
    const unitGroup = escapedUnits.length
      ? `(?:${escapedUnits.join("|")})`
      : "";

    const regexes: RegExp[] = [];
    templates.forEach((tpl) => {
      try {
        const pattern = tpl
          .replace(PAYMENT_KEYWORDS_BAR, paymentGroup)
          .replace(CURRENCY_BAR, currencyGroup)
          .replace(UNIT_BAR, unitGroup);
        regexes.push(new RegExp(pattern, "gi"));
      } catch {
        //
      }
    });
    return regexes;
  }, [boldKeywordsData, paymentKeyword, currency, unit]);

  const getBoldKeywordsList = useCallback(() => {
    if (
      articleSection === SECTION_KEYS.ctg &&
      !!entryData &&
      entryData?.CTG !== undefined
    ) {
      const originatorList =
        entryData.CTG?.["Drug Profile"]?.flatMap((tableData) => {
          return (
            tableData["Originator/Developer"].value
              ?.flatMap((item) => item.text)
              ?.filter((text) => !!text.trim()) || []
          );
        }) || [];

      return [...originatorList, ...additionalBoldKeywords];
    }
    if (classificationManual === "" || articleSection === "") return [];

    const enableBoldSectionList = [
      SECTION_KEYS.deals,
      SECTION_KEYS.dealFiling,
      SECTION_KEYS.fundingFiling,
      SECTION_KEYS.ventureCapital,
      SECTION_KEYS.expeditedProgram,
      SECTION_KEYS.regulatory,
      SECTION_KEYS.clinicalTrialStatus,
      SECTION_KEYS.clinicalTrial,
      SECTION_KEYS.publicationJournal,
      SECTION_KEYS.conferenceSchedule,
    ] as unknown as ArticleSection[];

    if (!enableBoldSectionList.includes(articleSection)) return [];

    if (
      !(
        [
          SECTION_KEYS.deals,
          SECTION_KEYS.ventureCapital,
          SECTION_KEYS.dealFiling,
          SECTION_KEYS.fundingFiling,
        ] as unknown as ArticleSection[]
      ).includes(articleSection) &&
      !entryData[SECTION_KEYS.clinicalTrialStatus] &&
      !entryData[SECTION_KEYS.clinicalTrial] &&
      !entryData[SECTION_KEYS.expeditedProgram] &&
      !entryData[SECTION_KEYS.regulatory] &&
      !entryData[SECTION_KEYS.publicationJournal] &&
      !entryData[SECTION_KEYS.conferenceSchedule]
    ) {
      return [...additionalBoldKeywords];
    }

    if (SECTION_KEYS.clinicalTrial && entryData[SECTION_KEYS.clinicalTrial]) {
      const originatorList =
        entryData[SECTION_KEYS.clinicalTrial]?.["Drug Profile"]?.flatMap(
          (tableData) => {
            return (
              tableData["Originator/Developer"].value
                ?.flatMap((item) => item.text)
                ?.filter((text) => !!text.trim()) || []
            );
          },
        ) || [];

      return [...originatorList, ...additionalBoldKeywords];
    }

    if (
      SECTION_KEYS.clinicalTrialStatus &&
      entryData[SECTION_KEYS.clinicalTrialStatus]
    ) {
      const originatorList =
        entryData[SECTION_KEYS.clinicalTrialStatus]?.["Drug Profile"]?.flatMap(
          (tableData) => {
            return (
              tableData["Originator/Developer"].value
                ?.flatMap((item) => item.text)
                ?.filter((text) => !!text.trim()) || []
            );
          },
        ) || [];

      return [...originatorList, ...additionalBoldKeywords];
    }

    if (
      SECTION_KEYS.publicationJournal &&
      entryData[SECTION_KEYS.publicationJournal]
    ) {
      const originatorList =
        entryData[SECTION_KEYS.publicationJournal]?.["Drug Profile"]?.flatMap(
          (tableData) => {
            return (
              tableData["Originator/Developer"].value
                ?.flatMap((item) => item.text)
                ?.filter((text) => !!text.trim()) || []
            );
          },
        ) || [];

      return [...originatorList, ...additionalBoldKeywords];
    }

    if (
      SECTION_KEYS.conferenceSchedule &&
      entryData[SECTION_KEYS.conferenceSchedule]
    ) {
      const originatorList =
        entryData[SECTION_KEYS.conferenceSchedule]?.["Drug Profile"]?.flatMap(
          (tableData) => {
            return (
              tableData["Originator/Developer"].value
                ?.flatMap((item) => item.text)
                ?.filter((text) => !!text.trim()) || []
            );
          },
        ) || [];

      return [...originatorList, ...additionalBoldKeywords];
    }

    if (SECTION_KEYS.regulatory && entryData[SECTION_KEYS.regulatory]) {
      const originatorList =
        entryData[SECTION_KEYS.regulatory]?.["Drug Profile"]?.flatMap(
          (tableData) => {
            return (
              tableData["Originator/Developer"].value
                ?.flatMap((item) => item.text)
                ?.filter((text) => !!text.trim()) || []
            );
          },
        ) || [];

      const authorizedCompanyList =
        entryData[SECTION_KEYS.regulatory]?.["Approval Process Contents"]?.[
          "Approval Process"
        ]?.flatMap((tableData) => {
          return (
            tableData["Authorized Company"].value
              ?.flatMap((item) => item.text)
              ?.filter((text) => !!text.trim()) || []
          );
        }) || [];

      return [
        ...originatorList,
        ...authorizedCompanyList,
        ...additionalBoldKeywords,
      ];
    }

    if (
      SECTION_KEYS.expeditedProgram &&
      entryData[SECTION_KEYS.expeditedProgram]
    ) {
      const originatorList =
        entryData[SECTION_KEYS.expeditedProgram]?.["Drug Profile"]?.flatMap(
          (tableData) => {
            return (
              tableData["Originator/Developer"].value
                ?.flatMap((item) => item.text)
                ?.filter((text) => !!text.trim()) || []
            );
          },
        ) || [];
      const designatedCompanyName =
        entryData[SECTION_KEYS.expeditedProgram]?.[
          "Expedited Program Contents"
        ]?.["Designated Company name"]?.value
          ?.flatMap((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];

      return [
        ...originatorList,
        ...designatedCompanyName,
        ...additionalBoldKeywords,
      ];
    }

    if (SECTION_KEYS.deals && entryData[SECTION_KEYS.deals]) {
      const dealNameList =
        entryData[SECTION_KEYS.deals]?.["Deal Type & Status"]?.[
          "Deal Name"
        ]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];
      const licensorNameList =
        entryData[SECTION_KEYS.deals]?.["Licensor Table"]?.[
          "Licensor Name"
        ]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];
      const licenseeNameList =
        entryData[SECTION_KEYS.deals]?.["Licensee Table"]?.flatMap(
          (tableData) =>
            tableData["Licensee Name"]?.value
              ?.map((item) => item.text)
              ?.filter((text) => !!text.trim()) || [],
        ) || [];

      return [...dealNameList, ...licenseeNameList, ...licensorNameList];
    }

    if (SECTION_KEYS.dealFiling && entryData[SECTION_KEYS.dealFiling]) {
      const dealNameList =
        entryData[SECTION_KEYS.dealFiling]?.["Deal Type & Status"]?.[
          "Deal Name"
        ]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];
      const licensorNameList =
        entryData[SECTION_KEYS.dealFiling]?.["Licensor Table"]?.[
          "Licensor Name"
        ]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];
      const licenseeNameList =
        entryData[SECTION_KEYS.dealFiling]?.["Licensee Table"]?.flatMap(
          (tableData) =>
            tableData["Licensee Name"]?.value
              ?.map((item) => item.text)
              ?.filter((text) => !!text.trim()) || [],
        ) || [];

      return [...dealNameList, ...licenseeNameList, ...licensorNameList];
    }

    if (SECTION_KEYS.ventureCapital && entryData[SECTION_KEYS.ventureCapital]) {
      const targetData = entryData[SECTION_KEYS.ventureCapital];
      const roundNameList =
        targetData?.["Round Type & Status"]?.["Round Name"]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];
      const roundTypeList =
        targetData?.["Round Type & Status"]?.["Round Type"]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];
      const companyNameList =
        targetData?.["Funded Company"]?.["Company Name"]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];

      const investorsList =
        targetData?.Investors?.["Investor Name"]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];

      return [
        ...roundNameList,
        ...roundTypeList,
        ...companyNameList,
        ...investorsList,
      ];
    }

    if (SECTION_KEYS.fundingFiling && entryData[SECTION_KEYS.fundingFiling]) {
      const targetData = entryData[SECTION_KEYS.fundingFiling];
      const roundNameList =
        targetData?.["Round Type & Status"]?.["Round Name"]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];
      const roundTypeList =
        targetData?.["Round Type & Status"]?.["Round Type"]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];
      const companyNameList =
        targetData?.["Funded Company"]?.["Company Name"]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];

      const investorsList =
        targetData?.Investors?.["Investor Name"]?.value
          ?.map((item) => item.text)
          ?.filter((text) => !!text.trim()) || [];

      return [
        ...roundNameList,
        ...roundTypeList,
        ...companyNameList,
        ...investorsList,
      ];
    }

    return [];
  }, [articleSection, entryData, classificationManual, additionalBoldKeywords]);

  const boldKeywordsList = useMemo(() => {
    const dbKeywords =
      boldKeywordsData?.map((keyword: BoldKeywordItem) => {
        const nameToString = String(keyword.name).trim();
        if (keyword.isPayment === true) {
          return nameToString
            .replace(PAYMENT_KEYWORDS_BAR, currencyKeywordsBar)
            .replace(CURRENCY_BAR, currencyUnitBar)
            .replace(UNIT_BAR, unitBar)
            .trim();
        }
        return nameToString;
      }) || [];

    return [...dbKeywords, ...getBoldKeywordsList()];
  }, [
    boldKeywordsData,
    currencyKeywordsBar,
    currencyUnitBar,
    unitBar,
    getBoldKeywordsList,
  ]);

  const checkBoldKeyword = useCallback(
    (text: string): [number, number, boolean][] => {
      if (!boldKeywordsList.length) return [];

      const boldRanges = generateBoldRanges(text, boldKeywordsList);

      let amountRanges: BoldRange[] = [];
      let paymentRanges: BoldRange[] = [];
      if (
        articleSection === SECTION_KEYS.deals ||
        articleSection === SECTION_KEYS.dealFiling
      ) {
        amountRanges = [];
        amountRegexes.forEach((regex) => {
          const r = new RegExp(regex.source, regex.flags);
          let match: RegExpExecArray | null;
          // eslint-disable-next-line no-cond-assign
          while ((match = r.exec(text)) !== null) {
            const start = match.index;
            const end = start + match[0].length;
            amountRanges.push({ start, end, type: "bold" });
          }
        });

        paymentRanges = [];
        paymentTemplateRegexes.forEach((regex) => {
          const r = new RegExp(regex.source, regex.flags);
          let match: RegExpExecArray | null;
          // eslint-disable-next-line no-cond-assign
          while ((match = r.exec(text)) !== null) {
            const start = match.index;
            const end = start + match[0].length;
            paymentRanges.push({ start, end, type: "bold" });
          }
        });
      }

      const all = [...boldRanges, ...amountRanges, ...paymentRanges].sort(
        (a, b) => a.start - b.start,
      );
      if (!all.length) return [];
      const merged: BoldRange[] = [];
      let current = all[0];
      for (let i = 1; i < all.length; i++) {
        const next = all[i];
        if (next.start <= current.end) {
          current.end = Math.max(current.end, next.end);
        } else {
          merged.push(current);
          current = next;
        }
      }
      merged.push(current);

      return merged.map((range) => [range.start, range.end, true]);
    },
    [boldKeywordsList, amountRegexes, paymentTemplateRegexes, articleSection],
  );

  return {
    checkBoldKeyword,
  };
};
